// Порог фандинга: показ и ввод (task.txt, 2026-09-29; диапазон — 2026-09-30).
//
// Владелец просил три вещи, и все живут здесь:
//   1) поле «Юзеров на фандинг» в админке (раздел «Лидерборд») должно
//      РЕДАКТИРОВАТЬСЯ. Раньше в нём садилась единица и правка была невозможна —
//      см. parseTradersPerWave;
//   2) порог фандинга конкретного юзера должен быть виден рядом с его стартовым
//      депозитом, суммой сделки и плечом — см. ratesSummary/ratesSub;
//   3) «добавить параметр, чтобы выставлять диапазон порога фандинга для работы
//      алгоритмов» (задача владельца 2026-09-30, уточнено им же: «для алгоритма
//      доски») — ДИАПАЗОН порога алгоритма доски: верхняя граница (порог
//      отбора) и нижняя («не глубже»), см. parseThresholdRange.
//
// Модуль чистый: ни React, ни запросов. Поэтому его проверяет тест напрямую
// (TG-BOT/src/test_admin_funding_threshold.js), а не «на глаз» в браузере.

// ── 1. Поле «Юзеров на фандинг» ─────────────────────────────────────────────
//
// Границы те же, что понимает алгоритм: leaderboardSim берёт
// Math.min(tradersPerWaveSetting(), число участников), то есть значение больше
// состава доски бессмысленно, а меньше единицы — бессмысленно вдвойне.
export const LB_TPW_MIN = 1
export const LB_TPW_MAX = 100

// Разбор ЧЕРНОВИКА поля в число. В состоянии компонента должна лежать строка
// ровно такая, как её набрали (включая пустую) — иначе поле «дерётся» с тем,
// кто печатает: стереть старую цифру одним нажатием уже означает «пусто → 1»,
// и набрать новое число невозможно. Проверка делается один раз — при
// сохранении.
//
// Возвращает { ok: true, value } либо { ok: false, error } — текст для админа.
export function parseTradersPerWave(draft) {
  const s = String(draft ?? '').trim()
  if (!s) return { ok: false, error: 'Пусто — укажите целое число' }
  if (!/^\d+$/.test(s)) return { ok: false, error: 'Только целое число (без точки, минуса и букв)' }
  const n = parseInt(s, 10)
  if (n < LB_TPW_MIN || n > LB_TPW_MAX) {
    return { ok: false, error: `Допустимо от ${LB_TPW_MIN} до ${LB_TPW_MAX}` }
  }
  return { ok: true, value: n }
}

// ── 2. Диапазон порога фандинга алгоритма доски ─────────────────────────────
//
// Что это. Ставка фандинга попадает в волну доски, если она ВНУТРИ диапазона:
//   нижняя граница ≤ ставка ≤ верхняя граница.
// Верхняя — «порог отбора»: наименее глубокая ставка, которая ещё годится
// (дефолт −0.4 %, длинная сторона собирает фандинг на отрицательных ставках).
// Нижняя — «не брать слишком глубокие»: пусто = без ограничения.
// Границы те же, что понимает алгоритм (db.lbThresholdRange + проверка в
// adminConfig.sanitizePatch): проценты не глубже −10 и не больше 0 —
// положительная ставка в этом алгоритме не торгуется.
export const LB_RATE_MIN = -10
export const LB_RATE_MAX = 0

// Разбор ЧЕРНОВИКА одной границы. Держим в состоянии строку как её набрали
// (тот же принцип, что у parseTradersPerWave: иначе поле «дерётся» с тем, кто
// печатает — стереть минус, чтобы набрать новое число, уже означает ошибку).
//   optional: true — пусто ЗАКОННО, это «без границы» (null), а не ноль:
//   ноль срезал бы половину ставок молча.
// Возвращает { ok: true, value } (value = число либо null) или
// { ok: false, error } — текст для админа.
export function parseThresholdBound(draft, { optional = false, label = 'Граница' } = {}) {
  // Запятая как десятичный разделитель: в русской раскладке «−0,4» — обычное дело.
  const s = String(draft ?? '').trim().replace(',', '.')
  if (!s) {
    return optional
      ? { ok: true, value: null }
      : { ok: false, error: `${label}: укажите число, напр. -0.4` }
  }
  if (!/^-?\d+(\.\d+)?$/.test(s)) {
    return { ok: false, error: `${label}: только число (без букв и пробелов), напр. -0.4` }
  }
  const v = parseFloat(s)
  if (v > LB_RATE_MAX) {
    return { ok: false, error: `${label}: ставка фандинга отрицательная или 0 — допустимо от ${LB_RATE_MIN} до ${LB_RATE_MAX} (напр. -0.4)` }
  }
  if (v < LB_RATE_MIN) {
    return { ok: false, error: `${label}: допустимо не глубже ${LB_RATE_MIN}` }
  }
  return { ok: true, value: v }
}

// Разбор ДИАПАЗОНА целиком (обе границы). Проверяет ещё и то, что полоса не
// пустая: нижняя обязана быть ГЛУБЖЕ верхней (−1.5 глубже −0.4). При нижней
// выше верхней выборка пуста, и волна не начислит ни одной сделки — именно
// поэтому такое не сохраняется и на сервере (adminConfig.sanitizePatch).
// Возвращает { ok: true, upper, lower } или { ok: false, error }.
export function parseThresholdRange({ upper, lower }) {
  const u = parseThresholdBound(upper, { optional: false, label: 'Верхняя граница' })
  if (!u.ok) return u
  const l = parseThresholdBound(lower, { optional: true, label: 'Нижняя граница' })
  if (!l.ok) return l
  if (l.value !== null && l.value >= u.value) {
    return {
      ok: false,
      error: `Нижняя граница должна быть ГЛУБЖЕ верхней: при верхней ${u.value}% нижняя — например ${Math.round((u.value - 0.5) * 100) / 100}%`,
    }
  }
  return { ok: true, upper: u.value, lower: l.value }
}

// Текст диапазона для админки: «≤ −0.4%» (без нижней границы) либо
// «−1.5% … −0.4%» (по возрастанию, как читают полосу).
export function thresholdRangeText(upper, lower) {
  if (upper == null) return '—'
  if (lower == null) return `≤ ${rateText(upper)}`
  return `${rateText(lower)} … ${rateText(upper)}`
}

// ── 3. Показ порога фандинга ────────────────────────────────────────────────

// −0.6 → «−0.6 %». null/undefined/мусор → «—»: пустой порог — это не ноль,
// а «порог не задан» (тот же смысл, что в db.getActiveBotMinFundingRates).
export function rateText(rate) {
  if (rate == null) return '—'
  const v = Number(rate)
  if (!Number.isFinite(v)) return '—'
  return `${v > 0 ? '+' : ''}${v}%`
}

// ── 4. Ввод порога: минус стоит заранее (2026-10-21) ────────────────────────
//
// Просьба владельца: «должен заранее стоять минус. Чтобы пользователь не писал
// минус и чтобы не возникло каких-то непоняток, чтобы пользователь просто
// написал саму цифру, а минус заранее уже стоял в строке».
//
// Поэтому в полях порога фандинга (создание бота и редактирование) минус
// нарисован отдельным знаком слева и стереть его нельзя, а в самом поле лежит
// МОДУЛЬ числа: юзер пишет «0.6» — видит «−0.6». Знак возвращается один раз —
// при отправке на сервер (fundingSigned): обе границы диапазона ставок у нас
// отрицательные, положительный фандинг бот не берёт.

// Модуль значения для поля ввода: '-0.6' → '0.6', '' → ''. Мусор → fallback.
export function fundingMagnitude(value, fallback = '') {
  if (value === '' || value === null || value === undefined) return fallback
  const v = Number(String(value).trim().replace(',', '.'))
  if (!Number.isFinite(v)) return fallback
  return String(Math.abs(v))
}

// Что попадает в поле при наборе: только цифры и одна точка. Минус, плюс,
// пробелы и буквы отбрасываются — знак уже нарисован слева. Запятая (русская
// раскладка) становится точкой.
export function fundingDraft(raw) {
  const s = String(raw ?? '').replace(',', '.').replace(/[^0-9.]/g, '')
  const i = s.indexOf('.')
  if (i < 0) return s
  return s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '')
}

// Значение для сервера: модуль из поля → отрицательное число.
// Пусто → fallback (верхняя граница −0.6, нижняя — null = без границы).
export function fundingSigned(magnitude, fallback) {
  if (magnitude === '' || magnitude === null || magnitude === undefined) return fallback
  const v = Number(String(magnitude).trim().replace(',', '.'))
  if (!Number.isFinite(v)) return fallback
  // «-0» — не ошибка, но в отчётах и сравнениях выглядит странно: 0 остаётся 0.
  return v === 0 ? 0 : -Math.abs(v)
}

const STATUS_RU = { active: 'работает', stopped: 'остановлен', error: 'ошибка' }

const statusRu = (s) => STATUS_RU[s] || s || '—'

// Уникальные пороги по порядку появления — для короткой сводки.
function uniqueRates(list) {
  const out = []
  for (const r of list || []) {
    if (r && r.rate != null && Number.isFinite(Number(r.rate)) && !out.includes(Number(r.rate))) {
      out.push(Number(r.rate))
    }
  }
  return out
}

// Сводка порога фандинга юзера для строки «Порог фандинга» рядом с депозитом,
// суммой сделки и плечом. list — то, что отдаёт админка:
//   участник алгоритма — [{ rate, rateTo, source: 'board', label }] — диапазон
//                        волны: rate = верхняя граница, rateTo = нижняя
//                        (null = без нижней; task.txt, 2026-09-30);
//   реальный юзер      — [{ botId, name, exchange, status, rate, source:'bot' }].
// Возвращает { text, sub } — значение строки и подстрочник с расшифровкой.
export function ratesSummary(list) {
  const arr = Array.isArray(list) ? list : []
  const board = arr.find(r => r && r.source === 'board')
  if (board) {
    return {
      text: thresholdRangeText(board.rate, board.rateTo),
      sub: 'порог волны фандинга — один на весь алгоритм (правится выше, в «Доске»)',
    }
  }
  if (!arr.length) {
    return { text: '—', sub: 'ботов у юзера нет' }
  }
  const rates = uniqueRates(arr)
  if (!rates.length) {
    return {
      text: 'не задан',
      sub: `в параметрах ботов нет MinFundingRate (ботов: ${arr.length})`,
    }
  }
  const sub = arr
    .map(b => `${b.name || b.botId || 'бот'} · ${b.exchange || '—'} · ${rateText(b.rate)} (${statusRu(b.status)})`)
    .join(' · ')
  return { text: rates.map(rateText).join('  ·  '), sub }
}

// Короткая метка для списка участников доски: «порог −0.6 %».
export function ratesShort(list) {
  const { text } = ratesSummary(list)
  return text === '—' ? 'порог не задан' : `порог ${text}`
}
